import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ShoppingCart, Search } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import FallbackImage from '@/components/FallbackImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [selectedImage, setSelectedImage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => { fetchProduct(); }, [id]);

  useEffect(() => {
    if (product && product.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
    }
  }, [product]);

  // Search products as user types
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/products?search=${encodeURIComponent(searchQuery)}&limit=8`, { headers });
        setSearchResults(response.data.slice(0, 8));
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSearchSelect = (productId) => {
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults([]);
    navigate(`/product/${productId}`);
  };

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/products/${encodeURIComponent(id)}`, { headers });
      setProduct(response.data);
    } catch (error) {
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const priceFor = (size) => (((user && (user.role === 'admin' || user.approved) && size.pt_price) ? size.pt_price : size.pvpr_price) || 0);
  const keyOf = (sz) => sz.ean || sz.size;

  const setQty = (size, val) => {
    const max = size.stock > 0 ? size.stock : 0;
    const q = Math.max(0, Math.min(max, isNaN(val) ? 0 : val));
    setQuantities((prev) => ({ ...prev, [keyOf(size)]: q }));
  };

  const totalUnits = () => (selectedColor?.sizes || []).reduce((s, sz) => s + (quantities[keyOf(sz)] || 0), 0);
  const subtotal = () => (selectedColor?.sizes || []).reduce((s, sz) => s + (quantities[keyOf(sz)] || 0) * priceFor(sz), 0);

  const addToCart = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para agregar al carrito');
      navigate('/login');
      return;
    }
    const rows = (selectedColor?.sizes || []).filter((sz) => (quantities[keyOf(sz)] || 0) > 0);
    if (rows.length === 0) {
      toast.error('Introduce al menos una cantidad');
      return;
    }
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    rows.forEach((sz) => {
      const qty = quantities[keyOf(sz)];
      const uniqueVariantId = sz.ean || `${product.id}-${selectedColor.color}-${sz.size}`;
      const idx = existingCart.findIndex((item) => item.variant_id === uniqueVariantId);
      const cartItem = {
        variant_id: uniqueVariantId,
        reference: sz.reference || sz.ean || 'N/A',
        ean: sz.ean,
        model_name: product.model_name,
        color: selectedColor.color,
        size: sz.size,
        quantity: qty,
        price: priceFor(sz),
        image: (product.images && product.images[0]) || ''
      };
      if (idx > -1) existingCart[idx].quantity += qty;
      else existingCart.push(cartItem);
    });
    localStorage.setItem('cart', JSON.stringify(existingCart));
    toast.success(`¡${rows.length} talla(s) añadidas al carrito!`);
    setQuantities({});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600 text-lg">Cargando producto...</p>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600 text-lg">Producto no encontrado</p>
      </div>
    );
  }

  const isPending = user && user.role !== 'admin' && user.role !== 'manager' && !user.approved;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-black border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2 text-white hover:text-blue-400 transition">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Volver al catálogo</span>
              <span className="sm:hidden">Catálogo</span>
            </Link>
            
            {/* Quick Search - Hidden on mobile */}
            <div className="relative flex-1 max-w-md mx-4 sm:mx-8 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar otro modelo o referencia..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500"
                  data-testid="product-detail-search"
                />
              </div>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleSearchSelect(result.id)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-left"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        {result.images && result.images[0] ? (
                          <img src={result.images[0]} alt={result.model_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingCart className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{result.model_name}</p>
                        <p className="text-sm text-blue-600">REF: {result.variants?.[0]?.reference || '-'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Click outside to close */}
              {showSearchResults && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowSearchResults(false)}
                />
              )}
            </div>
            
            <Link to="/" className="flex items-center space-x-3">
              <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10" />
              <span className="text-white text-xl font-bold">B2B</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Images */}
            <div>
              <div className="bg-gray-100 rounded-xl overflow-hidden mb-4" style={{ height: '500px' }}>
                <FallbackImage
                  images={product.images && product.images.length > 0 ? [product.images[selectedImage]] : []}
                  alt={product.model_name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-7 gap-2">
                  {product.images.slice(0, 7).map((image, index) => (
                    <button
                      key={index}
                      data-testid={`thumbnail-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`rounded-lg overflow-hidden border-2 transition ${selectedImage === index ? 'border-blue-600' : 'border-gray-200'}`}
                    >
                      <img src={image} alt={`${product.model_name} ${index + 1}`} className="w-full h-20 object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=No+Image'; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl font-bold text-black mb-2">{product.model_name}</h1>
                {product.description && <p className="text-gray-600">{product.description}</p>}
              </div>

              {/* Color Selection */}
              {product.colors && product.colors.length > 1 && (
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Color</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((colorOption, index) => (
                      <button
                        key={index}
                        data-testid={`color-option-${index}`}
                        onClick={() => { setSelectedColor(colorOption); setQuantities({}); }}
                        className={`px-4 py-2 rounded-lg border-2 transition ${selectedColor?.color === colorOption.color ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        {colorOption.color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sizes & quantities table */}
              {selectedColor && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-black">Tallas y cantidades {selectedColor.color ? `· ${selectedColor.color}` : ''}</h3>
                    <span className="text-xs uppercase tracking-wide text-gray-400 hidden sm:inline">Introduce todas las cantidades del tirón</span>
                  </div>
                  <div className="border border-gray-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[320px] text-sm" data-testid="sizes-table">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <tr>
                          <th className="text-left px-2 sm:px-4 py-3">Talla</th>
                          <th className="text-left px-2 sm:px-4 py-3 hidden sm:table-cell">Estado</th>
                          <th className="text-left px-2 sm:px-4 py-3">Precio</th>
                          <th className="text-right px-2 sm:px-4 py-3">Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedColor.sizes.map((sz) => {
                          const available = sz.stock > 0;
                          const q = quantities[keyOf(sz)] || 0;
                          return (
                            <tr key={keyOf(sz)} className={`border-t border-gray-100 ${available ? '' : 'bg-gray-50 text-gray-400'}`} data-testid={`size-row-${sz.size}`}>
                              <td className="px-2 sm:px-4 py-3 font-bold text-base">{sz.size}</td>
                              <td className="px-2 sm:px-4 py-3 hidden sm:table-cell">
                                {available ? <span className="text-green-600 font-medium">DISPONIBLE</span> : <span>NO STOCK</span>}
                              </td>
                              <td className="px-2 sm:px-4 py-3">
                                {!user ? '—' : available ? `€${priceFor(sz).toFixed(2)}` : '—'}
                              </td>
                              <td className="px-2 sm:px-4 py-3">
                                <div className="flex items-center justify-end gap-2">
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    className="h-10 w-10 sm:h-8 sm:w-8 text-lg font-bold touch-manipulation"
                                    disabled={!available || q <= 0}
                                    data-testid={`qty-dec-${sz.size}`}
                                    onClick={() => setQty(sz, q - 1)}
                                  >−</Button>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    min="0" 
                                    max={sz.stock}
                                    data-testid={`qty-input-${sz.size}`}
                                    disabled={!available}
                                    value={q}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                      const num = parseInt(val, 10) || 0;
                                      setQty(sz, Math.min(num, sz.stock));
                                    }}
                                    onFocus={(e) => e.target.select()}
                                    className="w-16 sm:w-14 text-center text-lg font-semibold border-2 border-gray-300 rounded-md py-2 disabled:bg-gray-100 disabled:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 touch-manipulation"
                                  />
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    className="h-10 w-10 sm:h-8 sm:w-8 text-lg font-bold touch-manipulation"
                                    disabled={!available || q >= sz.stock}
                                    data-testid={`qty-inc-${sz.size}`}
                                    onClick={() => setQty(sz, q + 1)}
                                  >+</Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="bg-black text-white rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-300">Total unidades</p>
                  <p className="text-2xl font-bold" data-testid="total-units">{totalUnits()}</p>
                </div>
                {user && (
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-gray-300">Subtotal</p>
                    <p className="text-2xl font-bold" data-testid="subtotal">€{subtotal().toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Add to cart */}
              {isPending ? (
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                  <p className="text-yellow-900 font-semibold mb-2">⏳ Cuenta Pendiente de Aprobación</p>
                  <p className="text-sm text-yellow-800">No puedes realizar pedidos hasta que tu cuenta sea aprobada por un administrador.</p>
                </div>
              ) : (
                <Button
                  data-testid="add-to-cart-btn"
                  onClick={addToCart}
                  disabled={totalUnits() === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg text-white disabled:bg-gray-400"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {totalUnits() === 0 ? 'Añadir al carrito (introduce cantidades)' : `Añadir ${totalUnits()} unidad(es) al carrito`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
